import { withPrefix } from 'gatsby'
import { deriveSeoFromResult, brandTitle } from './seo-utils'

function normalizePathname(p) {
  const u = new URL(p, window.location.origin)
  let s = u.pathname.replace(/\/+/g, '/')
  if (!s.startsWith('/')) s = `/${s}`
  return s === '/' ? '/' : (s.endsWith('/') ? s : `${s}/`)
}

function selByName(name) {
  return document.head.querySelector(`meta[name="${CSS.escape(name)}"]`)
}
function selByProp(prop) {
  return document.head.querySelector(`meta[property="${CSS.escape(prop)}"]`)
}
function upsertMetaName(name, content) {
  const existing = selByName(name)
  if (content == null || content === '') {
    if (existing) existing.parentNode.removeChild(existing)
    return
  }
  if (existing) {
    if (existing.getAttribute('content') !== String(content)) existing.setAttribute('content', String(content))
  } else {
    const m = document.createElement('meta')
    m.setAttribute('name', name)
    m.setAttribute('content', String(content))
    document.head.appendChild(m)
  }
}
function upsertMetaProp(prop, content) {
  const existing = selByProp(prop)
  if (content == null || content === '') {
    if (existing) existing.parentNode.removeChild(existing)
    return
  }
  if (existing) {
    if (existing.getAttribute('content') !== String(content)) existing.setAttribute('content', String(content))
  } else {
    const m = document.createElement('meta')
    m.setAttribute('property', prop)
    m.setAttribute('content', String(content))
    document.head.appendChild(m)
  }
}

export function applySeoToDocument(seo, brand = 'Harbor Capital') {
  const fullTitle = brandTitle(seo.title, brand)
  if (fullTitle && document.title !== fullTitle) document.title = fullTitle

  upsertMetaName('description', seo.description ?? '')
  upsertMetaName('keywords', Array.isArray(seo.keywords) ? seo.keywords.join(', ') : (seo.keywords ?? ''))
  if (seo.institutional) upsertMetaName('data-institutional', 'true')
  else upsertMetaName('data-institutional', '')

  const og = seo.openGraph || {}
  const ogPairs = {
    'og:type': og.type || 'website',
    'og:title': og.title || fullTitle || '',
    'og:description': og.description || seo.description || '',
    'og:url': og.url || window.location.href,
    'og:image': og.image || ''
  }
  Object.entries(ogPairs).forEach(([prop, content]) => upsertMetaProp(prop, content))
}

async function getResultViaLoader(pathname) {
  const l = window.___loader
  if (!l) return null
  try {
    const res = await l.loadPageData(pathname)
    if (res && (res.result || res.payload?.result || res.json?.result)) {
      return res.result || res.payload?.result || res.json?.result
    }
    const pr = await l.loadPage(pathname)
    if (pr && (pr.result || pr.payload?.result || pr.json?.result)) {
      return pr.result || pr.payload?.result || pr.json?.result
    }
  } catch {
    // intentionally empty: ignore errors
  }
  return null
}

async function getResultViaFetch(pathname) {
  try {
    const seg = pathname === '/' ? 'index' : pathname.replace(/^\//, '').replace(/\/$/, '')
    const url = withPrefix(`/page-data/${seg}/page-data.json`)
    const resp = await fetch(url, { credentials: 'same-origin', cache: 'force-cache' })
    if (!resp.ok) return null
    const json = await resp.json()
    return json?.result || null
  } catch {
    return null
  }
}

async function loadResultForPath(pathname) {
  const n = normalizePathname(pathname)
  let result = await getResultViaLoader(n)
  if (!result) result = await getResultViaFetch(n)
  if (!result) {
    await new Promise(r => setTimeout(r, 0))
    result = await getResultViaLoader(n) || await getResultViaFetch(n)
  }
  return { n, result }
}

export async function applySeoForPath(pathname, brand = 'Harbor Capital') {
  const { n, result } = await loadResultForPath(pathname)
  if (!result) return
  const seo = deriveSeoFromResult(result, n)
  applySeoToDocument(seo, brand)
  requestAnimationFrame(() => applySeoToDocument(seo, brand))
}
